import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import AdminDashboard from "./AdminDashboard";
import AgentApp from "./agents/AgentApp";
import OutfitChangePage from "./OutfitChangePage";
import { API } from "./components/app/constants";
import { NoiseOverlay } from "./components/shared";
import { Navigation } from "./components/shared/Navigation";
import { Footer } from "./components/shared/Footer";
import {
  LandingPage, StartPage, CreatePage, BackgroundPage,
  PricingPage, SuccessPage, SubscriptionSuccessPage,
  GalleryPage, CreateHub, AuthCallback, ResetPasswordPage,
  PrivacyPage
} from "./pages";

axios.defaults.withCredentials = true;

const AppRouter = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOAuthCallback, setIsOAuthCallback] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setIsOAuthCallback(true);
      setIsLoading(false);
      return;
    }
    
    const initUser = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
        localStorage.setItem("starframe_user", JSON.stringify(res.data));
      } catch {
        try {
          const storedUser = localStorage.getItem("starframe_user");
          if (storedUser) {
            const userData = JSON.parse(storedUser);
            const res = await axios.get(`${API}/users/${userData.id}`);
            setUser(res.data);
            localStorage.setItem("starframe_user", JSON.stringify(res.data));
          }
        } catch {
          localStorage.removeItem("starframe_user");
        }
      }
      setIsLoading(false);
    };
    
    initUser();
  }, []);

  const handleUserCreated = (userData) => {
    setUser(userData);
    setIsOAuthCallback(false);
    localStorage.setItem("starframe_user", JSON.stringify(userData));
  };

  const handleUserUpdate = (userData) => {
    setUser(userData);
    localStorage.setItem("starframe_user", JSON.stringify(userData));
  };

  const handleNavigate = (path) => navigate(path);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // continue with local logout
    }
    localStorage.removeItem("starframe_user");
    setUser(null);
    navigate("/");
  };

  if (isOAuthCallback) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <NoiseOverlay />
        <AuthCallback onUserCreated={handleUserCreated} onNavigate={handleNavigate} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <NoiseOverlay />
      <Navigation user={user} onNavigate={handleNavigate} onLogout={handleLogout} />
      
      {isLoading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner" />
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={user ? <CreateHub user={user} onUserUpdate={handleUserUpdate} onNavigate={handleNavigate} /> : <LandingPage onNavigate={handleNavigate} />} />
            <Route path="/hub" element={<CreateHub user={user} onUserUpdate={handleUserUpdate} onNavigate={handleNavigate} />} />
            <Route path="/start" element={<StartPage user={user} onUserCreated={handleUserCreated} onNavigate={handleNavigate} />} />
            <Route path="/create" element={<CreatePage user={user} onUserUpdate={handleUserUpdate} onNavigate={handleNavigate} />} />
            <Route path="/background" element={<BackgroundPage user={user} onUserUpdate={handleUserUpdate} onNavigate={handleNavigate} />} />
            <Route path="/outfit" element={<OutfitChangePage user={user} onUserUpdate={handleUserUpdate} onNavigate={handleNavigate} />} />
            <Route path="/pricing" element={<PricingPage user={user} onNavigate={handleNavigate} />} />
            <Route path="/success" element={<SuccessPage user={user} onUserUpdate={handleUserUpdate} onNavigate={handleNavigate} />} />
            <Route path="/subscription/success" element={<SubscriptionSuccessPage user={user} onUserUpdate={handleUserUpdate} onNavigate={handleNavigate} />} />
            <Route path="/gallery" element={<GalleryPage user={user} onUserUpdate={handleUserUpdate} onNavigate={handleNavigate} />} />
            <Route path="/reset-password" element={<ResetPasswordPage onNavigate={handleNavigate} />} />
            <Route path="/privacy" element={<PrivacyPage />} />
          </Routes>
        </AnimatePresence>
      )}
      <Footer onNavigate={handleNavigate} />
    </div>
  );
};

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/admin/*" element={<AdminDashboard />} />
          <Route path="/agents/*" element={<AgentApp />} />
          <Route path="/*" element={<AppRouter />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}
